/**
 * AccessibilityWidget — site-wide ADA / WCAG 2.1 helper.
 *
 * A floating "universal access" button (bottom-left) opens a panel of
 * toggles that restyle the whole app via classes on <html>:
 *   bigger text · high contrast · underline links · readable font ·
 *   reduce motion · strong focus outlines
 * Preferences persist in localStorage and re-apply on every load.
 */
import { useEffect, useRef, useState } from "react";
import {
  PersonStanding, X, Type, Contrast, Underline, CaseSensitive,
  PauseCircle, ScanEye, RotateCcw,
} from "lucide-react";

const STORAGE_KEY = "hp.acc";

const OPTIONS = [
  { key: "bigtext",   cls: "acc-bigtext",   label: "Bigger text",        icon: Type },
  { key: "contrast",  cls: "acc-contrast",  label: "High contrast",      icon: Contrast },
  { key: "underline", cls: "acc-underline", label: "Underline links",    icon: Underline },
  { key: "readable",  cls: "acc-readable",  label: "Readable font",      icon: CaseSensitive },
  { key: "nomotion",  cls: "acc-nomotion",  label: "Reduce motion",      icon: PauseCircle },
  { key: "focus",     cls: "acc-focus",     label: "Highlight focus",    icon: ScanEye },
];

function readPrefs() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {}; }
  catch { return {}; }
}

function applyPrefs(prefs) {
  const html = document.documentElement;
  for (const o of OPTIONS) html.classList.toggle(o.cls, !!prefs[o.key]);
}

export default function AccessibilityWidget() {
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState(readPrefs);
  const panelRef = useRef(null);

  useEffect(() => { applyPrefs(prefs); }, [prefs]);

  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") setOpen(false); }
    function onDoc(e) {
      if (open && panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDoc);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDoc);
    };
  }, [open]);

  function toggle(key) {
    setPrefs((p) => {
      const next = { ...p, [key]: !p[key] };
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  }

  function reset() {
    setPrefs({});
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  }

  const activeCount = OPTIONS.filter((o) => prefs[o.key]).length;

  return (
    <div className="hp-acc" ref={panelRef}>
      {open && (
        <div className="hp-acc__panel" role="dialog"
             aria-label="Accessibility options" data-testid="acc-panel">
          <div className="hp-acc__head">
            <span>Accessibility</span>
            <button type="button" onClick={() => setOpen(false)}
                    aria-label="Close accessibility panel"
                    data-testid="acc-close">
              <X size={16}/>
            </button>
          </div>
          <div className="hp-acc__opts">
            {OPTIONS.map(({ key, label, icon: Icon }) => (
              <button key={key} type="button"
                      onClick={() => toggle(key)}
                      aria-pressed={!!prefs[key]}
                      data-testid={`acc-toggle-${key}`}
                      className={`hp-acc__opt ${prefs[key] ? "is-on" : ""}`}>
                <Icon size={17}/>
                <span>{label}</span>
                <span className="hp-acc__state">{prefs[key] ? "On" : "Off"}</span>
              </button>
            ))}
          </div>
          <button type="button" onClick={reset} className="hp-acc__reset"
                  data-testid="acc-reset">
            <RotateCcw size={13}/> Reset all
          </button>
          <p className="hp-acc__note">WCAG 2.1 AA · settings are remembered on this device.</p>
        </div>
      )}
      <button type="button"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              aria-label="Accessibility options"
              title="Accessibility options"
              data-testid="acc-open-button"
              className={`hp-acc__fab ${activeCount ? "has-active" : ""}`}>
        <PersonStanding size={24}/>
        {activeCount > 0 && <span className="hp-acc__badge">{activeCount}</span>}
      </button>
    </div>
  );
}

/* ── styles + the global accessibility CSS modes ─────────────── */
if (typeof document !== "undefined" && !document.getElementById("hp-acc-css")) {
  const css = `
  .hp-acc { position:fixed; left:16px; bottom:16px; z-index:90;
    font-family:"Inter","Helvetica Neue",Arial,sans-serif; }
  .hp-acc__fab { display:flex; align-items:center; justify-content:center;
    width:48px; height:48px; border-radius:50%; border:none; cursor:pointer;
    background:#1B2333; color:#fff; box-shadow:0 4px 16px rgba(27,35,51,0.35);
    position:relative; transition:transform 0.15s ease, background-color 0.15s; }
  .hp-acc__fab:hover { transform:scale(1.06); background:#2C3448; }
  .hp-acc__fab.has-active { background:#C05746; }
  .hp-acc__badge { position:absolute; top:-3px; right:-3px; min-width:18px;
    height:18px; border-radius:9px; background:#fff; color:#C05746;
    font-size:11px; font-weight:700; display:flex; align-items:center;
    justify-content:center; padding:0 4px; border:1.5px solid #C05746; }
  .hp-acc__panel { position:absolute; left:0; bottom:58px; width:250px;
    background:#fff; border:1px solid rgba(27,35,51,0.18); border-radius:14px;
    box-shadow:0 12px 36px rgba(27,35,51,0.22); padding:12px; }
  .hp-acc__head { display:flex; justify-content:space-between; align-items:center;
    font-weight:700; font-size:14px; color:#121614; margin-bottom:8px; }
  .hp-acc__head button { border:none; background:none; cursor:pointer;
    color:#6C7285; padding:4px; display:flex; }
  .hp-acc__opts { display:grid; gap:4px; }
  .hp-acc__opt { display:flex; align-items:center; gap:8px; width:100%;
    padding:8px 10px; border:1px solid #E8E5DF; border-radius:9px;
    background:#FDFBF7; cursor:pointer; font-family:inherit; font-size:13.5px;
    color:#3A3F3B; transition:border-color 0.15s, background-color 0.15s; }
  .hp-acc__opt:hover { border-color:#C05746; }
  .hp-acc__opt.is-on { background:#FEF3E9; border-color:#C05746; color:#121614; font-weight:600; }
  .hp-acc__opt .hp-acc__state { margin-left:auto; font-size:11px;
    text-transform:uppercase; letter-spacing:0.06em; color:#9BA19C; }
  .hp-acc__opt.is-on .hp-acc__state { color:#C05746; }
  .hp-acc__reset { display:flex; align-items:center; gap:5px; margin-top:8px;
    border:none; background:none; cursor:pointer; font-family:inherit;
    font-size:12px; color:#6C7285; padding:4px 2px; }
  .hp-acc__reset:hover { color:#C05746; }
  .hp-acc__note { margin:6px 0 0; font-size:10.5px; color:#9BA19C; line-height:1.4; }

  /* ═══ Global accessibility modes (classes on <html>) ═══ */
  /* zoom (not font-size) so px-styled text scales too */
  html.acc-bigtext body { zoom: 1.22; }
  html.acc-contrast body { filter:contrast(1.25); }
  html.acc-underline a { text-decoration:underline !important; }
  html.acc-readable body, html.acc-readable body * {
    font-family:Arial, Helvetica, sans-serif !important; letter-spacing:0.01em; }
  html.acc-nomotion *, html.acc-nomotion *::before, html.acc-nomotion *::after {
    animation:none !important; transition:none !important; scroll-behavior:auto !important; }
  html.acc-focus *:focus { outline:3px solid #C05746 !important; outline-offset:2px !important; }
  `;
  const style = document.createElement("style");
  style.id = "hp-acc-css";
  style.textContent = css;
  document.head.appendChild(style);
}
